// ============================================================================
// Lists API
// ============================================================================
// API functions for managing task lists and list tabs
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';

// ============================================================================
// TASK LISTS CRUD
// ============================================================================

/**
 * Fetch all task lists visible to the current user
 * @returns {Promise<Array>} Array of task list objects
 */
export async function fetchTaskLists() {
    try {
        const { data, error } = await supabase
            .from('task_lists')
            .select(`
                *,
                task_list_tabs (
                    id,
                    key,
                    label,
                    icon,
                    sort_order,
                    tab_filters,
                    default_columns,
                    default_grouping,
                    default_pinned_columns,
                    default_sort,
                    is_service_tab,
                    service_ids,
                    is_event_tab,
                    event_types,
                    is_tracking_tab,
                    tracking_type
                ),
                task_list_shares (
                    profile_id
                ),
                profiles!task_lists_created_by_fkey (
                    user_id,
                    full_name,
                    short_name
                )
            `)
            .order('created_at', { ascending: false });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('[Lists API] Error fetching task lists:', error);
        throw error;
    }
}

/**
 * Fetch a single task list with tabs, by id or by friendly slug.
 *
 * Passing `{ slug }` resolves the slug and fetches the row in ONE round trip -
 * there is no separate id-lookup query on friendly-URL navigation. Slugs are
 * stored normalized (lowercase); soft-deleted lists never match a slug.
 *
 * @param {string|{slug: string}} ref - list UUID, or { slug } for a friendly slug
 * @returns {Promise<Object|null>} List object with tabs, or null when not found
 */
export async function fetchTaskList(ref) {
    const slug = (typeof ref === 'object' && ref !== null)
        ? (ref.slug || '').trim().toLowerCase()
        : null;
    if (slug === '') return null;

    try {
        let query = supabase
            .from('task_lists')
            .select(`
                *,
                task_list_tabs (
                    id,
                    key,
                    label,
                    icon,
                    sort_order,
                    tab_filters,
                    default_columns,
                    default_grouping,
                    default_pinned_columns,
                    default_sort,
                    is_service_tab,
                    service_ids,
                    is_event_tab,
                    event_types,
                    is_tracking_tab,
                    tracking_type
                ),
                task_list_shares (
                    profile_id
                ),
                profiles!task_lists_created_by_fkey (
                    user_id,
                    full_name,
                    short_name
                )
            `);
        query = slug
            ? query.eq('slug', slug).eq('is_deleted', false)
            : query.eq('id', ref);
        const { data, error } = await query.maybeSingle();

        if (error) {
            // 22P02 = a malformed (non-UUID) id from the URL - that is "not
            // found", not a crash; the raw Postgres message echoes the URL
            // value and must never reach an error screen.
            if (error.code === '22P02') return null;
            throw error;
        }
        if (!data) return null;

        // Sort tabs by sort_order
        if (data.task_list_tabs) {
            data.task_list_tabs.sort((a, b) => a.sort_order - b.sort_order);
        }

        return data;
    } catch (error) {
        console.error('[Lists API] Error fetching task list:', error);
        throw error;
    }
}

/**
 * Create a new task list
 * @param {Object} listData - List data
 * @param {string} listData.name - List name
 * @param {string} listData.description - List description
 * @param {string} listData.icon - Font Awesome icon
 * @param {Object} listData.base_filters - Base filters (JSON)
 * @param {Array<string>} listData.default_columns - Default visible columns
 * @param {Object} listData.default_sort - Default sort settings
 * @param {string} listData.default_grouping - Default grouping key
 * @param {boolean} listData.is_shared - Whether list is shared with all users
 * @param {Array<string>} listData.shared_with - Array of user IDs to share with (uses junction table)
 * @param {Array<Object>} tabs - Array of tab objects (optional)
 * @returns {Promise<Object>} Created list object
 */
export async function createTaskList(listData, tabs = []) {
    try {
        const userId = auth.user?.id;
        if (!userId) throw new Error('User not authenticated');

        // Create the list
        const { data: list, error: listError } = await supabase
            .from('task_lists')
            .insert({
                name: listData.name,
                slug: listData.slug || null,
                description: listData.description || null,
                icon: listData.icon || 'fa-list',
                created_by: userId,
                sharing_type: listData.is_shared ? 'public' : 'private',
                base_filters: listData.base_filters || {},
                default_columns: listData.default_columns || [
                    'client', 'service', 'period', 'status', 'due_date', 'assignee', 'billing_status'
                ],
                default_sort: listData.default_sort || { field: 'due_date', direction: 'asc' },
                default_grouping: listData.default_grouping || null
            })
            .select()
            .single();

        if (listError) throw listError;

        // Create shares in junction table if provided
        const sharedWith = listData.shared_with || [];
        if (sharedWith.length > 0) {
            const sharesToInsert = sharedWith.map(profileId => ({
                task_list_id: list.id,
                profile_id: profileId
            }));
            const { error: sharesError } = await supabase
                .from('task_list_shares')
                .insert(sharesToInsert);

            if (sharesError) console.error('[Lists API] Error creating shares:', sharesError);
        }

        // Create tabs if provided
        if (tabs && tabs.length > 0) {
            const tabsToInsert = tabs.map((tab, index) => ({
                list_id: list.id,
                key: tab.key,
                label: tab.label,
                icon: tab.icon || null,
                sort_order: tab.sort_order !== undefined ? tab.sort_order : index,
                tab_filters: tab.tab_filters || {},
                default_columns: tab.default_columns || null,
                default_grouping: tab.default_grouping || null,
                default_pinned_columns: tab.default_pinned_columns || [],
                default_sort: tab.default_sort || { field: 'due_date', direction: 'asc' },
                is_service_tab: tab.is_service_tab || false,
                service_ids: tab.service_ids || [],
                is_event_tab: tab.is_event_tab || false,
                event_types: tab.event_types || [],
                is_tracking_tab: tab.is_tracking_tab || false,
                tracking_type: tab.tracking_type || null
            }));

            const { error: tabsError } = await supabase
                .from('task_list_tabs')
                .insert(tabsToInsert);

            if (tabsError) throw tabsError;
        }

        // Fetch the complete list with tabs
        return await fetchTaskList(list.id);
    } catch (error) {
        console.error('[Lists API] Error creating task list:', error);
        throw error;
    }
}

/**
 * Update a task list
 * @param {string} listId - UUID of the list
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated list object
 */
export async function updateTaskList(listId, updates) {
    try {
        // Convert is_shared to sharing_type if present (for consistency with createTaskList)
        const processedUpdates = { ...updates };
        if ('is_shared' in processedUpdates) {
            processedUpdates.sharing_type = processedUpdates.is_shared ? 'public' : 'private';
            delete processedUpdates.is_shared;
        }

        // Extract shared_with for junction table handling
        const sharedWith = processedUpdates.shared_with;
        delete processedUpdates.shared_with;

        const { data, error } = await supabase
            .from('task_lists')
            .update({
                ...processedUpdates,
                updated_at: new Date().toISOString()
            })
            .eq('id', listId)
            .select()
            .single();

        if (error) throw error;

        // Sync shares in junction table if provided
        if (Array.isArray(sharedWith)) {
            await syncTaskListShares(listId, sharedWith);
        }

        return data;
    } catch (error) {
        console.error('[Lists API] Error updating task list:', error);
        throw error;
    }
}

/**
 * Sync shares for a task list (replace all with new set)
 * @param {string} listId - UUID of the list
 * @param {Array<string>} profileIds - Array of profile user IDs to share with
 */
async function syncTaskListShares(listId, profileIds) {
    try {
        // Delete all existing shares for this list
        const { error: deleteError } = await supabase
            .from('task_list_shares')
            .delete()
            .eq('task_list_id', listId);

        if (deleteError) throw deleteError;

        // Insert new shares
        if (profileIds.length > 0) {
            const sharesToInsert = profileIds.map(profileId => ({
                task_list_id: listId,
                profile_id: profileId
            }));
            const { error: insertError } = await supabase
                .from('task_list_shares')
                .insert(sharesToInsert);

            if (insertError) throw insertError;
        }
    } catch (error) {
        console.error('[Lists API] Error syncing task list shares:', error);
        throw error;
    }
}

/**
 * Delete a task list (cascades to tabs)
 * @param {string} listId - UUID of the list
 * @returns {Promise<void>}
 */
export async function deleteTaskList(listId) {
    try {
        const { error } = await supabase
            .from('task_lists')
            .delete()
            .eq('id', listId);

        if (error) throw error;
    } catch (error) {
        console.error('[Lists API] Error deleting task list:', error);
        throw error;
    }
}

// ============================================================================
// TASK LIST TABS CRUD
// ============================================================================

/**
 * Create a new tab for a list
 * @param {string} listId - UUID of the list
 * @param {Object} tabData - Tab data
 * @returns {Promise<Object>} Created tab object
 */
export async function createTaskListTab(listId, tabData) {
    try {
        const { data, error } = await supabase
            .from('task_list_tabs')
            .insert({
                list_id: listId,
                key: tabData.key,
                label: tabData.label,
                icon: tabData.icon || null,
                sort_order: tabData.sort_order || 0,
                tab_filters: tabData.tab_filters || {},
                default_columns: tabData.default_columns || null,
                default_grouping: tabData.default_grouping || null,
                default_sort: tabData.default_sort || { field: 'due_date', direction: 'asc' },
                is_service_tab: tabData.is_service_tab || false,
                service_ids: tabData.service_ids || [],
                is_event_tab: tabData.is_event_tab || false,
                event_types: tabData.event_types || [],
                is_tracking_tab: tabData.is_tracking_tab || false,
                tracking_type: tabData.tracking_type || null
            })
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('[Lists API] Error creating task list tab:', error);
        throw error;
    }
}

/**
 * Update a task list tab
 * @param {string} tabId - UUID of the tab
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated tab object
 */
export async function updateTaskListTab(tabId, updates) {
    try {
        const { data, error } = await supabase
            .from('task_list_tabs')
            .update(updates)
            .eq('id', tabId)
            .select()
            .single();

        if (error) throw error;

        // Bump the parent list's updated_at so every user's saved column layout is
        // invalidated and picks up the new tab defaults. The per-user preferences
        // cache key (getPreferencesKey in tasks-grid.js) is built from
        // task_lists.updated_at; a tab-only edit would otherwise never reach anyone
        // who already has a saved layout - they'd keep stale columns.
        if (data?.list_id) {
            await touchTaskList(data.list_id);
        }

        return data;
    } catch (error) {
        console.error('[Lists API] Error updating task list tab:', error);
        throw error;
    }
}

/**
 * Bump a list's updated_at. Used to invalidate every user's cached grid column
 * layout (keyed off task_lists.updated_at) after a change to the list or one of
 * its tabs. Best-effort: a failure here must not fail the caller's primary write.
 * @param {string} listId - UUID of the list
 * @returns {Promise<void>}
 */
async function touchTaskList(listId) {
    const { error } = await supabase
        .from('task_lists')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', listId);
    if (error) console.error('[Lists API] Error touching task list updated_at:', error);
}

/**
 * Delete a task list tab
 * @param {string} tabId - UUID of the tab
 * @returns {Promise<void>}
 */
export async function deleteTaskListTab(tabId) {
    try {
        const { error } = await supabase
            .from('task_list_tabs')
            .delete()
            .eq('id', tabId);

        if (error) throw error;
    } catch (error) {
        console.error('[Lists API] Error deleting task list tab:', error);
        throw error;
    }
}

// Filter grammar (parseFilterTokens / applyFiltersToQuery / matchesFilters /
// extractJsonbFilters) lives in features/tasks/utils/task-filter-engine.js -
// the ONE module both the server queries and the realtime row checks share.

// ============================================================================
// TASK LIST QUERIES (consolidated from taskListApi.js)
// ============================================================================

/**
 * Get task lists by specific IDs (optimized for sidebar pinned lists)
 * @param {Array<string>} ids - Array of task list IDs to fetch
 */
export async function getTaskListsByIds(ids) {
    try {
        if (!ids || ids.length === 0) {
            return { success: true, data: [], error: null };
        }

        const { data, error } = await supabase
            .from('task_lists')
            .select('id, name, icon, slug')
            .in('id', ids)
            .eq('is_deleted', false);

        if (error) throw error;

        return { success: true, data: data || [], error: null };
    } catch (error) {
        console.error('[Lists API] Failed to get task lists by IDs:', error);
        return { success: false, data: null, error };
    }
}

// ============================================================================
// PINNED TASK LISTS (in user profile)
// ============================================================================

/**
 * Get current user's pinned task lists
 * Uses cached auth.profile when available to avoid extra API calls
 */
export async function getPinnedTaskLists() {
    try {
        if (auth.profile?.pinned_task_lists !== undefined) {
            return { success: true, data: auth.profile.pinned_task_lists || [], error: null };
        }

        const currentUserId = auth.user?.id;
        if (!currentUserId) throw new Error('User not authenticated');

        const { data, error } = await supabase
            .from('profiles')
            .select('pinned_task_lists')
            .eq('user_id', currentUserId)
            .maybeSingle();

        if (error) throw error;

        return { success: true, data: data?.pinned_task_lists || [], error: null };
    } catch (error) {
        console.error('[Lists API] Failed to get pinned task lists:', error);
        return { success: false, data: null, error };
    }
}

/**
 * Update pinned task lists for current user
 * @param {Array<string>} taskListIds - Array of task list IDs (max 10)
 */
export async function updatePinnedTaskLists(taskListIds) {
    try {
        const currentUserId = auth.user?.id;
        if (!currentUserId) throw new Error('User not authenticated');

        const limitedIds = taskListIds.slice(0, 25);

        const { data, error } = await supabase
            .from('profiles')
            .update({ pinned_task_lists: limitedIds })
            .eq('user_id', currentUserId)
            .select('pinned_task_lists')
            .maybeSingle();

        if (error) throw error;

        if (auth.profile) {
            auth.profile.pinned_task_lists = data?.pinned_task_lists || [];
        }

        return { success: true, data: data?.pinned_task_lists || [], error: null };
    } catch (error) {
        console.error('[Lists API] Failed to update pinned task lists:', error);
        return { success: false, data: null, error };
    }
}

/**
 * Pin a task list (add to pinned list)
 * Validates existing pins and removes stale IDs before adding
 */
export async function pinTaskList(taskListId) {
    try {
        const { data: current } = await getPinnedTaskLists();
        const currentPinned = current || [];

        if (currentPinned.includes(taskListId)) {
            return { success: true, data: currentPinned, error: null };
        }

        // Clean stale IDs: validate DB list IDs still exist
        const { PROGRAMMED_LISTS } = await import('features/lists/config/programmed-lists.js');
        const programmedIds = new Set(PROGRAMMED_LISTS.map(p => p.id));
        const dbIds = currentPinned.filter(id => !programmedIds.has(id));

        let validDbIds = new Set();
        if (dbIds.length > 0) {
            const result = await getTaskListsByIds(dbIds);
            if (result.success && result.data) {
                validDbIds = new Set(result.data.map(d => d.id));
            }
        }

        const cleanedPinned = currentPinned.filter(id =>
            programmedIds.has(id) || validDbIds.has(id)
        );

        const newPinned = [...cleanedPinned, taskListId].slice(0, 25);
        return await updatePinnedTaskLists(newPinned);
    } catch (error) {
        console.error('[Lists API] Failed to pin task list:', error);
        return { success: false, data: null, error };
    }
}

/**
 * Unpin a task list (remove from pinned list)
 */
export async function unpinTaskList(taskListId) {
    try {
        const { data: current } = await getPinnedTaskLists();
        const currentPinned = current || [];

        const newPinned = currentPinned.filter(id => id !== taskListId);
        return await updatePinnedTaskLists(newPinned);
    } catch (error) {
        console.error('[Lists API] Failed to unpin task list:', error);
        return { success: false, data: null, error };
    }
}

